import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import type { CustomerDto, RfqDto } from "@grizzly/shared";
import { api, ApiClientError } from "../../lib/apiClient.js";
import { useI18n } from "../../i18n/index.js";
import { Button } from "../ui/button.js";
import { Card } from "../ui/card.js";
import { Modal } from "../ui/modal.js";
import { SelectInput } from "../ui/field.js";
import { useToast } from "../../lib/useToast.js";

export function QuoteCreateDialog({
  open,
  onOpenChange,
  onCreated,
}: {
  open: boolean;
  onOpenChange(open: boolean): void;
  onCreated(): void;
}) {
  const { push } = useToast();
  const { t } = useI18n();
  const navigate = useNavigate();
  const [customers, setCustomers] = useState<CustomerDto[]>([]);
  const [rfqs, setRfqs] = useState<RfqDto[]>([]);
  const [customerId, setCustomerId] = useState("");
  const [rfqId, setRfqId] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    api
      .listCustomers()
      .then((r) => setCustomers(r.items))
      .catch(() => undefined);
    api
      .listRfqs()
      .then((r) => setRfqs(r.items.filter((x) => x.status === "open")))
      .catch(() => undefined);
  }, [open]);

  const submit = useCallback(async () => {
    setBusy(true);
    try {
      const created = await api.createQuote({ customerId, rfqId: rfqId || undefined });
      push(t("sales.createdToast"), created.quote.number);
      onOpenChange(false);
      setRfqId("");
      onCreated();
      navigate(`/quotes/${created.quote.id}`);
    } catch (err) {
      if (err instanceof ApiClientError) push(err.message, undefined, "danger");
    } finally {
      setBusy(false);
    }
  }, [customerId, navigate, onCreated, onOpenChange, push, rfqId, t]);

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title={t("sales.startQuote")}
      footer={
        <>
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            {t("common.cancel")}
          </Button>
          <Button variant="primary" onClick={submit} disabled={busy || !customerId}>
            {busy ? t("common.creating") : t("sales.createQuote")}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <SelectInput
          label={t("common.fields.customer")}
          options={customers.map((c) => ({
            value: c.id,
            label: c.displayName || c.legalName,
          }))}
          value={customerId}
          onChange={(e) => setCustomerId(e.target.value)}
        />
        <SelectInput
          label={t("sales.fromInquiry")}
          options={[
            { value: "", label: t("common.none") },
            ...rfqs.map((r) => ({
              value: r.id,
              label: `${r.number} · ${r.customerName}`,
            })),
          ]}
          value={rfqId}
          onChange={(e) => setRfqId(e.target.value)}
        />
        <Card className="border-dashed bg-surface-inset/40">
          <p className="text-sm text-ink-muted">{t("sales.linesHint")}</p>
        </Card>
      </div>
    </Modal>
  );
}
